import { Injectable } from "@angular/core";
import * as Prismic from "prismic-javascript";
import { PrismicConfig } from "src/prismic-config";
import { RichText, PrismicDOM } from "prismic-dom";
import Cookies from "js-cookie";
import { environment } from "src/environments/environment";
import { HttpClient } from "@angular/common/http";
import { lastValueFrom } from "rxjs";
import brandInfo  from "src/environments/brand.json";
import { MDBModalService } from "ng-uikit-pro-standard";
import { ModalTechnicalDifficultyComponent } from "../modal-technical-difficulty-error/modal-technical-difficulty-error.component";

const Elements = RichText.Elements;

@Injectable({
  providedIn: "root",
})
export class PrismicService {
  constructor(private modalService: MDBModalService, private httpClient: HttpClient) {
    this.modalService = modalService;
  }

  private parseStoredPrismicEnv(rawEnv: string): any | null {
    try {
      const parsed = JSON.parse(rawEnv);
      return this.sanitizePrismicEnv(parsed);
    } catch {
      return null;
    }
  }

  static async getEndpointAndToken(token) {
    console.log('check here ray 2')
    let splitToken = token.split("/");

    let baseUrl = splitToken[2];

    const env = environment.production ? "prod" : "dev";

    let prismicEnv;

    // Use environment.apiUrl so API calls hit the backend (e.g. localhost:8080), not the dev server (4200).
    let response = await fetch(`${environment.apiUrl}/v1/api/content/envs/all`);
    let prismicEnvs = await response.json();

    console.log("prismicEnvs: ", prismicEnvs);

    try {
      prismicEnv = prismicEnvs.filter((env) => env.baseUrl === baseUrl)[0];
      // console.log("Storing prismicEnv: ", prismicEnv);

      sessionStorage.setItem("prismicEnv", JSON.stringify(prismicEnv));

    } catch (error) {
      console.log("Error get prismic envs: ", error);
    }

    let newHeader = {};
    newHeader[baseUrl] = { preview: token };

    localStorage.setItem("Prismic-ref", JSON.stringify(newHeader));
    localStorage.setItem("Prismic-baseUrl", prismicEnv.baseUrl);
    sessionStorage.setItem("prismicEnv", JSON.stringify(prismicEnv));

    console.log("%c localStorage set Prismic-ref: ", "color: #1ECBE1", JSON.stringify(newHeader));
    console.log("%c localStorage set Prismic-baseUrl: ", "color: #1ECBE1", prismicEnv.baseUrl);

    return prismicEnv;
  }

  getEnvironment(env) {
    // Get Environment from Session Storage
    let _prismicEnv = sessionStorage.getItem("prismicEnv");

    if (_prismicEnv) {
      const safePrismicEnv = this.parseStoredPrismicEnv(_prismicEnv);
      if (safePrismicEnv) {
        return safePrismicEnv;
      }
      sessionStorage.removeItem("prismicEnv");
    }

    console.log("prismicEnv fetching from api...");

    // Set the environment

    this.fetchEnvironment(env).then((prismicEnv) => {
      const safePrismicEnv = this.sanitizePrismicEnv(prismicEnv);
      if (!safePrismicEnv) {
        return;
      }
      console.log("prismicEnv...", safePrismicEnv);
      sessionStorage.setItem("prismicEnv", JSON.stringify(safePrismicEnv));

      return safePrismicEnv;
    }).catch((e) => {
      this.modalService.show(ModalTechnicalDifficultyComponent, { class: "modal-dialog-centered" });
    })


  }

  /**
   * Validates the structure of prismicEnv object to prevent injection attacks
   * @param obj - The object to validate
   * @returns boolean - true if valid, false otherwise
   */
  private isValidPrismicEnv(obj: any): boolean {
    if (!obj || typeof obj !== 'object') {
      return false;
    }

    // Check for required properties with expected types
    const requiredProps = {
      apiEndpoint: 'string',
      accessToken: 'string',
      baseUrl: 'string'
    };

    for (const [prop, expectedType] of Object.entries(requiredProps)) {
      if (!(prop in obj) || typeof obj[prop] !== expectedType) {
        return false;
      }
    }

    // Additional validation: check that URLs are properly formatted
    try {
      new URL(obj.apiEndpoint);
      new URL(obj.baseUrl);
    } catch {
      return false;
    }

    return true;
  }

  /**
   * Allowlist validation for Google Maps API key that is later used in a script URL.
   */
  getValidatedGoogleMapsKey(prismicEnv: any): string | null {
    if (!prismicEnv || typeof prismicEnv !== "object") {
      return null;
    }

    const candidate = prismicEnv.googleMapsKey;
    if (typeof candidate !== "string") {
      return null;
    }

    const key = candidate.trim();
    if (!key || key.length > 512) {
      return null;
    }

    // Block dangerous characters/control bytes while allowing normal API-key formats.
    if (/[\u0000-\u001F\u007F<>"'`\\]/.test(key)) {
      return null;
    }

    return key;
  }

  private sanitizePrismicEnv(obj: any): any | null {
    if (!obj || typeof obj !== "object") {
      return null;
    }

    const apiEndpoint = this.normalizeHttpUrl(obj.apiEndpoint);
    const accessToken = this.sanitizeOpaqueToken(obj.accessToken);
    const baseUrl = this.normalizePrismicBaseUrl(obj.baseUrl || obj.baseurl);
    if (!apiEndpoint || !accessToken || !baseUrl) {
      return null;
    }

    const safeEnv: any = {
      apiEndpoint,
      accessToken,
      baseUrl,
      baseurl: baseUrl,
    };

    const googleMapsKey = this.getValidatedGoogleMapsKey(obj);
    if (googleMapsKey) {
      safeEnv.googleMapsKey = googleMapsKey;
    }

    const payPalConfig = this.getValidatedPaypalConfig(obj);
    if (payPalConfig) {
      safeEnv.payPalConfig = {
        client_id: payPalConfig.clientId,
        merchant_id: payPalConfig.merchantId,
      };
    }

    return safeEnv;
  }

  private normalizeHttpUrl(value: unknown): string | null {
    if (typeof value !== "string") {
      return null;
    }
    try {
      const url = new URL(value.trim());
      if (!["http:", "https:"].includes(url.protocol)) {
        return null;
      }
      return url.toString();
    } catch {
      return null;
    }
  }

  private normalizePrismicBaseUrl(value: unknown): string | null {
    if (typeof value !== "string") {
      return null;
    }

    const raw = value.trim();
    if (!raw || raw.length > 512) {
      return null;
    }

    const asUrl = this.normalizeHttpUrl(raw);
    if (asUrl) {
      return asUrl;
    }

    // Prismic env baseUrl is often just a hostname (no protocol).
    if (/^[A-Za-z0-9.-]+(?::\d{1,5})?$/.test(raw)) {
      return raw;
    }

    return null;
  }

  private sanitizeOpaqueToken(value: unknown): string | null {
    if (typeof value !== "string") {
      return null;
    }

    const token = value.trim();
    if (!token || token.length > 2048) {
      return null;
    }

    if (/[\u0000-\u001F\u007F<>"'`\\]/.test(token)) {
      return null;
    }

    return token;
  }

  /**
   * Sanitization boundary for the PayPal portion of `prismicEnv`.
   *
   * `prismicEnv` originates from untrusted client storage
   * (`sessionStorage["prismicEnv"]`) and its `client_id` / `merchant_id`
   * values eventually flow into a `<script>` tag's `src` query string in
   * {@link CheckoutBillingInformationComponent.initPaypalButton}. This
   * function is the single validation/sanitization chokepoint: every field
   * returned here is a freshly constructed, allowlist-sanitized string.
   * Do NOT read PayPal identifiers from `prismicEnv` elsewhere — always
   * route through this method so the trust boundary stays explicit.
   */
  getValidatedPaypalConfig(
    prismicEnv: unknown
  ): { clientId: string; merchantId: string } | null {
    if (!prismicEnv || typeof prismicEnv !== "object") {
      return null;
    }

    const payPalConfig = (prismicEnv as Record<string, unknown>).payPalConfig;
    if (!payPalConfig || typeof payPalConfig !== "object") {
      return null;
    }

    const rawConfig = payPalConfig as Record<string, unknown>;
    const clientId = this.sanitizePaypalToken(rawConfig.client_id);
    const merchantId = this.sanitizePaypalToken(rawConfig.merchant_id);
    if (!clientId || !merchantId) {
      return null;
    }

    return { clientId, merchantId };
  }

  /**
   * Allowlist-based sanitizer for PayPal identifiers.
   *
   * Uses `String.prototype.replace` with a negated character class so any
   * byte outside the allowlist is stripped — a pattern static-analysis
   * tools (e.g. Checkmarx) recognize as an explicit sanitization step.
   * If anything was stripped we reject the value entirely rather than
   * silently returning a weakened token.
   */
  private sanitizePaypalToken(value: unknown): string | null {
    if (typeof value !== "string") {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed || trimmed.length > 256) {
      return null;
    }

    // Strip anything outside the PayPal-ID allowlist. Allowed chars:
    // A-Z, a-z, 0-9, '.', '_', ',', '*', '-' (covers single and
    // comma-separated merchant IDs and client IDs).
    const sanitized = trimmed.replace(/[^A-Za-z0-9._,*-]/g, "");

    // Refuse to accept a value that contained disallowed characters —
    // the caller either misconfigured the env or is an attacker.
    if (!sanitized || sanitized !== trimmed) {
      return null;
    }

    return sanitized;
  }

  async fetchEnvironment(env) {
    let response = await fetch(`${environment.apiUrl}/v1/api/content/envs?env=${env}&brand=${brandInfo.brand}`);
    let prismicEnv = response.json();
    return prismicEnv;
  }

  // Get the API object which we will use for all our queries
  async getApi(token): Promise<void | any> {
    // console.log("getApi token: ", token);
    let prismicEnv = await PrismicService.getEndpointAndToken(token);

    return Prismic.api(prismicEnv.apiEndpoint, { accessToken: prismicEnv.accessToken })
      .then((api) => {
        console.log("Got Prismic API:", api);
        return api;
      })
      .catch((e) => console.log(`Error during connection to your Prismic API: ${e}`));
  }

  static initApp() {
    return () => {
      //  console.log("window.location.href", window.location.href);

      const queryString = window.location.search;

      const urlParams = new URLSearchParams(queryString);
      //  console.log(urlParams.get("token"));

      if (urlParams.has("token")) {
        return PrismicService.getEndpointAndToken(urlParams.get("token"));
      } else {
        return PrismicService.storePrismicRef();
      }

      // return PrismicService.storePrismicRef();
    };
  }

  isPreviewMode() {
    const previewRef = Cookies.get("io.prismic.preview");

    if (previewRef) {
      const parsedCookie = JSON.parse(previewRef);
      const isPreview = !!(parsedCookie["sgidigital-dev.prismic.io"] || parsedCookie["sgidigital.prismic.io"]);
      return isPreview;
    } else {
      return false;
    }
  }

  static storePrismicRef = async () => {
    console.log('%c **** prismicRef **** ', 'color: #188977')



    // Get the Prismic Env from API
    const env = environment.production ? "prod" : "dev";
    let response, prismicEnv;

    try {
      // console.log("Storing prismicEnv: ", prismicEnv);
      // Use relative URL to go through Express proxy (avoids CORS issues)
      response = await fetch(`${environment.apiUrl}/v1/api/content/envs?env=${env}&brand=${brandInfo.brand}`);
      prismicEnv = await response.json();
      sessionStorage.setItem("prismicEnv", JSON.stringify(prismicEnv));
    } catch (error) {
      console.log("Error get prismic envs: ", error);
      return null
    }

    let apiData = await Prismic.api(prismicEnv.apiEndpoint, { accessToken: prismicEnv.accessToken });
    const masterRef = apiData.refs.find((ref) => ref.isMasterRef === true);
    const prismicRef = masterRef.ref;

    const previewRef = Cookies.get("io.prismic.preview");

    if (previewRef) {
      const parsedCookie = JSON.parse(previewRef);
      const prismic_env = parsedCookie["sgidigital-dev.prismic.io"] || parsedCookie["sgidigital.prismic.io"] ? "Preview Mode" : "Master Mode";
      const prismicRef = parsedCookie["sgidigital-dev.prismic.io"] || parsedCookie["sgidigital.prismic.io"] ? previewRef : masterRef.ref;
      localStorage.setItem("Prismic-ref", prismicRef);

      // console.log('Prismic cookieUrl:',PrismicConfig.cookieUrl) // the undefined value
      // console.log('Prismic PreviewRef:',parsedCookie) // the undefined value
      // console.log('Entering into:',prismic_env) // the undefined value
      // console.log("Prismic-ref: ", prismicRef);
      // console.log('%c ***** ', 'color: #188977')
    } else {
      localStorage.setItem("Prismic-ref", prismicRef);
      localStorage.setItem("Prismic-baseUrl", prismicEnv.baseurl);
      // console.log("Prismic-ref: ", prismicRef);
      // console.log('Prismic-baseUrl', PrismicConfig.baseurl);
      // console.log('%c ***** ', 'color: #188977')
    }

    return prismicRef;
  };

  // Use the Link Resolver to return the correct url for your previews
  getPreviewUrl(token: string): Promise<string> {
    return this.getApi(token).then((api) => {
      console.log("getApi api: ", api);
      return api
        .previewSession(token, PrismicConfig.linkResolver, "/")
        .then((url) => {
          console.log("previewSession Found url: ", url);
          return url;
        })
        .catch((error) => {
          console.log("previewSession Caught Error from getPreviewUrl", error);
          return "/";
        });
    });
  }

  getHtml(content, vehicleObject = null) {
    // Custom string interpolation
    if (!content) {
      return;
    }
    let htmlContent = RichText.asHtml(content, PrismicConfig.linkResolver, this.htmlSerializer);

    if (vehicleObject) {
      htmlContent = htmlContent.replace("{Brand}", vehicleObject.make.name);
      htmlContent = htmlContent.replace("{Year}", vehicleObject.year);
      htmlContent = htmlContent.replace("{Model}", vehicleObject.model.name);
      htmlContent = htmlContent.replace("{0,000}", "5,000");
      htmlContent = htmlContent.replace("{Trim}", vehicleObject.trim);
    }

    return htmlContent;
  }

  linkResolver(doc): string {
    switch (doc.type) {
      case 'homepage':
        return '/';
      case 'blog_post':
        return `/blog/${doc.uid}`;
      case 'about_page':
        return '/about';
      default:
        return '/';
    }
  }


  htmlSerializer(type, element, content, children) {
    switch (type) {
      case Elements.heading1:
        return `<h1>${children.join("")}</h1>`;
      case Elements.heading2:
        return `<h2>${children.join("")}</h2>`;
      case Elements.heading3:
        return `<h3>${children.join("")}</h3>`;
      case Elements.heading4:
        return `<h4>${children.join("")}</h4>`;
      case Elements.heading5:
        return `<h5>${children.join("")}</h5>`;
      case Elements.heading6:
        return `<h6>${children.join("")}</h6>`;
      case Elements.paragraph:
        return `<p>${children.join("")}</p>`;
      case Elements.preformatted:
        return `<div>${children.join("")}</div>`; // Change Preformatted Prismic content to DIV structure
      case Elements.strong:
        return `<strong>${children.join("")}</strong>`;
      case Elements.em:
        return `<em>${children.join("")}</em>`;
      case Elements.subscript:
        return `<sub>${children.join("")}</sub>`;
      case Elements.superscript:
        return `<sup>${children.join("")}</sup>`;
      case Elements.mark:
        return `<mark>${children.join("")}</mark>`;
      case Elements.small:
        return `<small>${children.join("")}</small>`;
      case Elements.deleted:
        return `<del>${children.join("")}</del>`;
      case Elements.inserted:
        return `<ins>${children.join("")}</ins>`;
      case Elements.italics:
        return `<i>${children.join("")}</i>`;
      case Elements.underline:
        return `<u>${children.join("")}</u>`;
      case Elements.listItem:
        return `<li>${children.join("")}</li>`;
      case Elements.oListItem:
        return `<li>${children.join("")}</li>`;
      case Elements.list:
        return `<ul>${children.join("")}</ul>`;
      case Elements.oList:
        return `<ol>${children.join("")}</ol>`;
      case Elements.image:
        var linkUrl = element.linkTo ? PrismicDOM.Link.url(element.linkTo, this.linkResolver) : null;
        // var linkUrl = element.linkTo ? PrismicDOM.Link.url(element.linkTo, module.exports.linkResolver) : null;
        let linkTarget = element.linkTo && element.linkTo.target ? `target="${element.linkTo.target}" rel="noopener"` : "";
        let wrapperClassList = [element.label || "", "block-img"];
        let img = `<img src="${element.url}" alt="${element.alt || ""}" copyright="${element.copyright || ""}">`;
        return `
        <p class="${wrapperClassList.join(" ")}">
          ${linkUrl ? `<a ${linkTarget} href="${linkUrl}">${img}</a>` : img}
        </p>
      `;
      case Elements.embed:
        return `
        <div data-oembed="${element.oembed.embed_url}"
          data-oembed-type="${element.oembed.type}"
          data-oembed-provider="${element.oembed.provider_name}"
        >
          ${element.oembed.html}
        </div>
      `;
      case Elements.hyperlink:
        let target = element.data.target ? `target="${element.data.target}" rel="noopener"` : "";
        //  console.log("element.data:", element.data);
        // var linkUrl = PrismicDOM.Link.url(element.data, PrismicConfig.linkResolver);
        var linkUrl = element.data.url;
        return `<a ${target} href="${linkUrl}">${children.join("")}</a>`;
      case Elements.label:
        let label = element.data.label ? ` class="${element.data.label}"` : "";
        return `<span ${label}>${children.join("")}</span>`;
      case Elements.span:
        return content ? content.replace(/\n/g, "<br />") : "";
      default:
        return null;
    }
  }
}
